import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterBar, FilterPills, FilterSelect } from '@/components/feature/FilterBar';
import AiGeneratorModal from '@/pages/gcse/mock-exams/components/AiGeneratorModal';
import ALMockPaperCard from '@/pages/alevel/mock-exams/components/ALMockPaperCard';

export type ALBoard = 'AQA' | 'OCR';

export interface ALMockPaper {
  id: string;
  board: ALBoard;
  paper: string;
  title: string;
  set: string;
  marks: number;
  duration: string;
  topics: string[];
  level: 'A-Level' | 'AS-Level';
  downloads: number;
  rating: number;
  hasMarkScheme: boolean;
}

export const alMockPapers: ALMockPaper[] = [
  {
    id: 'al1', board: 'AQA', paper: 'Paper 1', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 100, duration: '2h 30m',
    topics: ['Computational Thinking', 'Problem Solving', 'Data Structures'],
    level: 'A-Level', downloads: 4821, rating: 4.9, hasMarkScheme: true,
  },
  {
    id: 'al2', board: 'AQA', paper: 'Paper 2', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 100, duration: '2h 30m',
    topics: ['Computer Systems', 'Networks', 'Databases', 'Big Data'],
    level: 'A-Level', downloads: 4503, rating: 4.8, hasMarkScheme: true,
  },
  {
    id: 'al3', board: 'AQA', paper: 'Paper 1', title: 'Mock Exam — Set B',
    set: 'Set B', marks: 100, duration: '2h 30m',
    topics: ['Algorithms', 'Theory of Computation', 'Functional Programming'],
    level: 'A-Level', downloads: 3756, rating: 4.7, hasMarkScheme: true,
  },
  {
    id: 'al4', board: 'AQA', paper: 'Paper 2', title: 'Mock Exam — Set B',
    set: 'Set B', marks: 100, duration: '2h 30m',
    topics: ['Cyber Security', 'Artificial Intelligence', 'Legal & Ethical Issues'],
    level: 'A-Level', downloads: 3287, rating: 4.6, hasMarkScheme: true,
  },
  {
    id: 'al5', board: 'AQA', paper: 'Paper 1', title: 'Mock Exam — Set C',
    set: 'Set C', marks: 100, duration: '2h 30m',
    topics: ['OOP', 'Data Representation', 'Computer Systems'],
    level: 'A-Level', downloads: 2912, rating: 4.5, hasMarkScheme: false,
  },
  {
    id: 'al6', board: 'AQA', paper: 'Paper 1', title: 'AS Mock Exam — Set A',
    set: 'Set A', marks: 70, duration: '1h 30m',
    topics: ['Computational Thinking', 'Data Structures', 'Computer Systems'],
    level: 'AS-Level', downloads: 2341, rating: 4.7, hasMarkScheme: true,
  },
  {
    id: 'al7', board: 'AQA', paper: 'Paper 2', title: 'AS Mock Exam — Set A',
    set: 'Set A', marks: 70, duration: '1h 30m',
    topics: ['Networks', 'Databases', 'Algorithms'],
    level: 'AS-Level', downloads: 2108, rating: 4.6, hasMarkScheme: true,
  },
  {
    id: 'al8', board: 'OCR', paper: 'Paper 1', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 100, duration: '2h 30m',
    topics: ['Computational Thinking', 'Problem Solving', 'Programming'],
    level: 'A-Level', downloads: 2641, rating: 4.8, hasMarkScheme: true,
  },
  {
    id: 'al9', board: 'OCR', paper: 'Paper 2', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 100, duration: '2h 30m',
    topics: ['Computer Systems', 'Networks', 'Databases'],
    level: 'A-Level', downloads: 2308, rating: 4.7, hasMarkScheme: true,
  },
  {
    id: 'al10', board: 'OCR', paper: 'Paper 1', title: 'Mock Exam — Set B',
    set: 'Set B', marks: 100, duration: '2h 30m',
    topics: ['Algorithms', 'Theory of Computation', 'Cyber Security'],
    level: 'A-Level', downloads: 1876, rating: 4.6, hasMarkScheme: true,
  },
  {
    id: 'al11', board: 'OCR', paper: 'Paper 1', title: 'AS Mock Exam — Set A',
    set: 'Set A', marks: 70, duration: '1h 30m',
    topics: ['Computational Thinking', 'Data Structures', 'Computer Systems'],
    level: 'AS-Level', downloads: 1543, rating: 4.5, hasMarkScheme: true,
  },
  {
    id: 'al12', board: 'OCR', paper: 'Paper 2', title: 'AS Mock Exam — Set A',
    set: 'Set A', marks: 70, duration: '1h 30m',
    topics: ['Networks', 'Databases', 'Algorithms'],
    level: 'AS-Level', downloads: 1234, rating: 4.4, hasMarkScheme: false,
  },
];

const boards: ALBoard[] = ['AQA', 'OCR'];

export default function ALevelMockExamsPage() {
  const [selectedBoard, setSelectedBoard] = useState<ALBoard | 'All'>('All');
  const [selectedLevel, setSelectedLevel] = useState<string>('All');
  const [showAiModal, setShowAiModal] = useState(false);

  const filtered = alMockPapers.filter((p) => {
    if (selectedBoard !== 'All' && p.board !== selectedBoard) return false;
    if (selectedLevel !== 'All' && p.level !== selectedLevel) return false;
    return true;
  });

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20advanced%20academic%20background%20with%20glowing%20circuit%20patterns%20and%20exam%20paper%20silhouettes%2C%20deep%20navy%20gradient%2C%20sophisticated%20geometric%20lines%20and%20nodes%2C%20emerald%20and%20white%20light%20accents%20on%20dark%20background%2C%20university%20level%20study%20atmosphere&width=1440&height=300&seq=al-mock1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">A Level Resources</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mock Exams &amp; Revision</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-2">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
                A Level Mock Exams &amp; Revision
              </h1>
              <p className="text-slate-400 text-base max-w-2xl mt-2">
                Full A Level and AS Level mock exam papers for AQA and OCR. Or generate a custom paper with AI — tailored to your specification, topics, and difficulty.
              </p>
            </div>
            <button
              onClick={() => setShowAiModal(true)}
              className="flex items-center gap-2 px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-sparkling-line text-base"></i>
              Generate with AI
            </button>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-file-paper-2-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">{alMockPapers.length} mock papers</span>
            </div>
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-check-double-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">Mark schemes included</span>
            </div>
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-sparkling-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">AI paper generator</span>
            </div>
          </div>
        </div>
      </section>

      {/* AI Banner */}
      <div className="bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                <i className="ri-sparkling-2-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Paper Generator</p>
                <p className="text-emerald-100 text-xs">Create a custom A Level mock exam in seconds — choose your board, topics, difficulty and number of questions.</p>
              </div>
            </div>
            <button
              onClick={() => setShowAiModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-magic-line text-sm"></i>
              Try AI Generator
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterBar count={filtered.length} countLabel="papers">
        <FilterPills
          label="Exam Board"
          value={selectedBoard}
          onChange={(v) => setSelectedBoard(v as ALBoard | 'All')}
          options={[
            { value: 'All', label: 'All Boards' },
            ...boards.map((b) => ({ value: b, label: b })),
          ]}
        />
        <div className="sm:ml-auto">
          <FilterSelect
            label="Qualification"
            value={selectedLevel}
            onChange={(v) => setSelectedLevel(v)}
            options={[
              { value: 'All', label: 'All Levels' },
              { value: 'A-Level', label: 'A Level' },
              { value: 'AS-Level', label: 'AS Level' },
            ]}
          />
        </div>
      </FilterBar>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((paper) => (
            <ALMockPaperCard key={paper.id} paper={paper} />
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-file-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No papers found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        )}
      </main>

      <Footer />

      {showAiModal && <AiGeneratorModal onClose={() => setShowAiModal(false)} level="A-Level" />}
    </div>
  );
}
